import { Chip, Stack, styled } from '@mui/material';
import { V2ThemeProvider } from '@onevision-resources/design-theme';
import { UserGroup } from '@provision/entity-types';
import {
  MaterialReactTable,
  MRT_RowVirtualizer,
  MRT_TableOptions,
  useMaterialReactTable,
} from 'material-react-table';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { PlatformUserAvatar } from 'src/components';
import { EllipsesMenu, EllipsesMenuOption } from 'src/components/EllipsesMenu/ellipses-menu';
import {
  ManageUserConversationAssigneeGroups,
  ServerPlatformUserRaw,
  SetUserPasswordParams,
  useGroupRules,
  useManageConversationAssigneeGroups,
  UserGroupRules,
} from 'src/graphql';
import {
  ManageUserToUserGroupParams,
  useManageUserPermissions,
} from 'src/graphql/entities/user-permissions';
import { Modals, useShowModals } from 'src/store/modals';
import { PlatformUserRaw } from 'src/types';
import { useScrollPaginateHandler } from 'src/utils/hooks';

export const StyledChip = styled(Chip, {
  name: 'StyledChip',
  shouldForwardProp: (prop) => prop !== 'isPublic',
})<{
  isPublic: boolean;
}>(({ isPublic, theme }) => ({
  backgroundColor: isPublic
    ? theme.palette.custom.OV.sys.surfaceContainerHighest
    : theme.palette.custom.OV.sys.successContainer,
  color: isPublic
    ? theme.palette.custom.OV.sys.onSurface
    : theme.palette.custom.OV.sys.onSuccessContainer,
}));

// once the user has scrolled within 300px of the bottom of the table, fetch more data if we can.
// 300 is a good number that will give some space to avoid long waitings for the users
const SCROLL_OFFSET_PX = 300;

const defaultColumn = {
  enableSorting: true,
  enableColumnActions: false,
};
const tableBackgroundStylingProps = {
  elevation: 0,
  sx: {
    height: '100%',
    borderRadius: 0,
  },
};
const columns = [
  {
    accessorKey: 'avatarFileKey',
    header: '',
    enableSorting: false,
    size: 56,
    Cell: ({ row }: { row: { original: Partial<PlatformUserRaw> } }) => {
      const user = row.original;
      const fullName = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() || '—';
      return (
        <PlatformUserAvatar
          name={fullName}
          partnerId={user.partnerId}
          avatarFileKey={user.avatarFileKey}
        />
      );
    },
  },
  {
    accessorKey: 'firstName',
    header: 'First Name',
  },
  {
    accessorKey: 'lastName',
    header: 'Last Name',
  },
  {
    accessorKey: 'email',
    header: 'Email',
  },
  {
    accessorKey: 'userGroups',
    header: 'Permissions',
    enableSorting: false,
    Cell: ({ renderedCellValue }: { renderedCellValue: Partial<UserGroup>[] }) => {
      const sortedGroups = [...(renderedCellValue || [])].sort((a, b) =>
        (a.name || '').localeCompare(b.name || ''),
      );
      return (
        <Stack direction="row" gap={0.5} flexWrap="wrap">
          {sortedGroups.map((item) => (
            <StyledChip
              isPublic={item.isPublic!}
              key={item.name}
              label={item.name}
              size="small"
            />
          ))}
        </Stack>
      );
    },
  },
  {
    accessorKey: 'conversationAssigneeGroups',
    header: 'Assignee Groups',
    enableSorting: false,
    Cell: ({ renderedCellValue }: { renderedCellValue: Partial<UserGroup>[] }) => {
      return (
        <Stack direction="row" gap={0.5} flexWrap="wrap">
          {renderedCellValue?.map((item) => (
            <Chip key={item.name} label={item.name} size="small" />
          ))}
        </Stack>
      );
    },
  },
];

export const UsersTable = ({
  users,
  onCopyId,
  onUpdateUser,
  onUpdateUserActiveState,
  onSetUserPassword,
  onSortChange,
  loadMore,
  hasMore,
  isLoading,
  sort,
}: {
  users: Partial<ServerPlatformUserRaw>[];
  onCopyId: (userId: string) => void;
  onUpdateUser: (data: Partial<PlatformUserRaw>) => void;
  onUpdateUserActiveState: (data: Partial<PlatformUserRaw>) => void;
  onSetUserPassword: (data: SetUserPasswordParams) => void;
  onSortChange?: (sort: { id: string; desc: boolean }) => void;
  loadMore: (limit?: number, localOffset?: number) => Promise<void>;
  hasMore?: boolean;
  isLoading?: boolean;
  sort?: { id: string; desc: boolean } | null;
}) => {
  const { groupRules } = useGroupRules();
  const { manageUserPermissions } = useManageUserPermissions();
  const { manageUserConversationAssigneeGroups } = useManageConversationAssigneeGroups();

  const isAdmin = useMemo(
    () => !!(groupRules[UserGroupRules.Admin] || groupRules[UserGroupRules.SuperAdmin]),
    [groupRules],
  );

  const canUpdateAssigneeGroups = useMemo(
    () => !!groupRules[UserGroupRules.AccessToUpdateAssigneeGroups],
    [groupRules],
  );

  const onUserUpdate = useCallback(
    (data: Partial<PlatformUserRaw>) => {
      onUpdateUser(data);
    },
    [onUpdateUser],
  );

  const onPermissionsUpdate = useCallback(
    (data: ManageUserToUserGroupParams) => {
      void manageUserPermissions(data);
    },
    [manageUserPermissions],
  );

  const onUserActiveStateChange = useCallback(
    (user: Partial<PlatformUserRaw>) => {
      onUpdateUserActiveState({ userId: user.id, isActive: !user.isActive });
    },
    [onUpdateUserActiveState],
  );

  const onResendInvite = useCallback(
    (userId: string, password?: string) => {
      onSetUserPassword({
        userId,
        temporaryPassword: password,
      });
    },
    [onSetUserPassword],
  );

  const onAssigneeGroupsUpdate = useCallback(
    (data: ManageUserConversationAssigneeGroups) => {
      void manageUserConversationAssigneeGroups(data);
    },
    [manageUserConversationAssigneeGroups],
  );

  const [
    showUpdateUserModal,
    showUpdateUserPermissionsModal,
    showResendInviteWithPasswordModal,
    showUpdateAssigneeGroupsModal,
  ] = useShowModals(
    Modals.UpdateUser,
    Modals.UpdateUserPermissions,
    Modals.ResentInviteWithPassword,
    Modals.UpdateAssigneeGroups,
  );

  const tableContainerRef = useRef<HTMLDivElement>(null);
  const rowVirtualizerInstanceRef = useRef<MRT_RowVirtualizer>(null);

  // https://www.material-react-table.com/docs/examples/infinite-scrolling
  useEffect(() => {
    if (!tableContainerRef.current) {
      return;
    }

    const { scrollHeight, scrollTop, clientHeight } = tableContainerRef.current;
    if (scrollHeight - scrollTop - clientHeight < SCROLL_OFFSET_PX && !isLoading && hasMore) {
      void loadMore();
    }
  }, [hasMore, isLoading, loadMore]);

  const handleScroll = useScrollPaginateHandler({
    loadMore,
    enabled: hasMore,
    thresholdPx: 100,
  });

  const config = useMemo(
    () =>
      ({
        columns,
        enableRowVirtualization: true,
        enableColumnVirtualization: true,
        data: users,
        defaultColumn,
        enableTopToolbar: false,
        manualSorting: true,
        enableBottomToolbar: false,
        enableRowActions: true,
        enablePagination: false,
        muiTablePaperProps: tableBackgroundStylingProps,
        enableStickyHeader: true,
        rowVirtualizerInstanceRef,
        rowVirtualizerOptions: { overscan: 4 },
        state: {
          showSkeletons: isLoading && users.length === 0,
          sorting: sort ? [sort] : [],
        },
        muiTableContainerProps: {
          ref: tableContainerRef,
          onScroll: handleScroll,
          sx: {
            // Clamp function is modification of original one (`clamp(350px, 0px + 100vh, 9999px)`).
            // It's changed to occupy 100% of space instead of 100% of view port.
            maxHeight: 'clamp(350px, 0px + 100%, 9999px)',
          },
        },
        muiTableHeadCellProps: {
          sx: (theme) => ({
            backgroundColor: theme.palette.custom.OV.sys.surfaceContainerLowest,
          }),
        },
        initialState: {
          columnPinning: {
            right: ['mrt-row-actions'],
          },
        },
        onSortingChange: onSortChange
          ? (updaterOrValue) => {
              const newSorting =
                typeof updaterOrValue === 'function'
                  ? updaterOrValue(sort ? [sort] : [])
                  : updaterOrValue;

              onSortChange?.(newSorting[0]);
              return newSorting;
            }
          : undefined,
        renderRowActions: ({ row, table }) => {
          const options: EllipsesMenuOption[] = [];
          const isUserActive = row.original.isActive;

          if (isAdmin && isUserActive) {
            options.push(
              {
                label: 'Update User',
                icon: 'pencil',
                iconLibrary: 'lucide',
                testId: 'users-table-update-user',
                onClick: () =>
                  showUpdateUserModal({
                    user: row.original,
                    onSubmit: onUserUpdate,
                  }),
              },
              {
                label: 'Update User permissions',
                icon: 'shield-check',
                iconLibrary: 'lucide',
                testId: 'users-table-update-permissions',
                onClick: () =>
                  showUpdateUserPermissionsModal({
                    user: row.original,
                    onSubmit: onPermissionsUpdate,
                  }),
              },
            );

            if (canUpdateAssigneeGroups) {
              options.push({
                label: 'Update Assignee Groups',
                icon: 'users',
                iconLibrary: 'lucide',
                testId: 'users-table-update-assignee-groups',
                onClick: () =>
                  showUpdateAssigneeGroupsModal({
                    user: row.original,
                    onSubmit: onAssigneeGroupsUpdate,
                  }),
              });
            }

            options.push(
              {
                label: 'Re-Send invite',
                icon: 'mail',
                iconLibrary: 'lucide',
                onClick: () => onResendInvite(row.original.id!),
              },
              {
                label: 'Re-Send invite with password',
                icon: 'key',
                iconLibrary: 'lucide',
                testId: 'users-table-resend-invite-with-password',
                onClick: () =>
                  showResendInviteWithPasswordModal({
                    onSubmit: onResendInvite,
                    userId: row.original.id!,
                  }),
              },
            );
          }

          options.push({
            label: 'Copy User ID',
            icon: 'copy',
            iconLibrary: 'lucide',
            onClick: () => onCopyId(row.original.id!),
          });

          if (isAdmin && isUserActive) {
            options.push({
              label: 'Deactivate User',
              icon: 'user-minus',
              iconLibrary: 'lucide',
              danger: true,
              onClick: () => onUserActiveStateChange(row.original),
            });
          }

          if (isAdmin && !isUserActive) {
            options.push({
              label: 'Activate User',
              icon: 'user-check',
              iconLibrary: 'lucide',
              onClick: () => onUserActiveStateChange(row.original),
            });
          }

          return (
            <V2ThemeProvider>
              <EllipsesMenu options={options} title={table.options.localization.rowActions} />
            </V2ThemeProvider>
          );
        },
      }) as MRT_TableOptions<Partial<PlatformUserRaw>>,
    [
      users,
      onCopyId,
      isLoading,
      handleScroll,
      isAdmin,
      sort,
      onUserUpdate,
      showUpdateUserModal,
      showUpdateUserPermissionsModal,
      showUpdateAssigneeGroupsModal,
      onPermissionsUpdate,
      onUserActiveStateChange,
      showResendInviteWithPasswordModal,
      onResendInvite,
      onAssigneeGroupsUpdate,
      onSortChange,
      canUpdateAssigneeGroups,
    ],
  );

  const table = useMaterialReactTable(config);
  return <MaterialReactTable table={table} />;
};
